import { Suspense, createElement, useEffect, useMemo, useRef, type ComponentType, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { WindowContext, useWindowShell, type WindowHandle } from './context'
import { withoutRef, type WindowNavigationState } from './navigation'
import type { WindowShell, WindowView } from './shell'
import type { WindowSourceMark } from './types'
import { useWindowStack } from './useWindowStack'

/** Рамка модального окна — примитив ui-kit, который передаёт приложение. */
export interface WindowFrameProps {
  title: string
  size: NonNullable<WindowView['size']>
  /** Чьё окно: рамка помечает его источником и оттенком; `null` — окно без источника. */
  source: WindowSourceMark | null
  /** Ресурс окна (`rid`), если окно ресурсное: рамка дописывает к источнику его имя. */
  resourceId: string | null
  /** Окно накрыто другим: неактивно целиком (inert), но смонтировано. */
  covered: boolean
  onClose: () => void
  children: ReactNode
}

/**
 * Окна поверх экрана из `?w=`. Неизвестная ссылка (старая, опечатка) ничего не
 * показывает и ничего не ломает: экран под ней остаётся рабочим.
 */
export function WindowHost({ Frame }: { Frame: ComponentType<WindowFrameProps> }) {
  const shell = useWindowShell()
  const { refs } = useWindowStack()
  const views = refs
    .map((ref) => ({ ref, view: shell.parseRef(ref) }))
    .filter((item): item is { ref: string; view: WindowView } => item.view !== undefined)

  return (
    <>
      {views.map(({ ref, view }, at) => {
        // Старая ссылка `?w=` на рабочее окно или страницу раздела (UI-RMK-FLOW, раздел 1): окно открывается на своём адресе.
        if (view.onAddress) return <MoveToAddress key={ref} reference={ref} view={view} shell={shell} />
        const covered = at < views.length - 1
        // Под окном, открытым поверх, остаётся смонтированным только окно во весь
        // экран (сканер): иначе перезапустилась бы камера (карточка 267).
        if (covered && view.size !== 'screen') return null
        return <StackedWindow key={ref} shell={shell} reference={ref} view={view} covered={covered} Frame={Frame} />
      })}
    </>
  )
}

/** Ссылка `?w=` на рабочее окно: заменить переходом на его адрес; «откуда пришли» — где был человек без этой ссылки. */
function MoveToAddress({ reference, view, shell }: { reference: string; view: WindowView; shell: WindowShell }) {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const state: WindowNavigationState = { windowFrom: `${pathname}${withoutRef(search, reference)}` }
    void navigate(shell.pathOf(view.kind, view.params), { replace: true, state })
  }, [navigate, pathname, reference, search, shell, view])
  return null
}

function StackedWindow({
  shell,
  reference,
  view,
  covered,
  Frame,
}: {
  shell: WindowShell
  reference: string
  view: WindowView
  covered: boolean
  Frame: ComponentType<WindowFrameProps>
}) {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const { close, replace } = useWindowStack()
  const guard = useRef<(() => boolean) | null>(null)

  const handle = useMemo<WindowHandle>(
    () => ({
      kind: view.kind,
      identity: view.identity,
      presentation: 'modal',
      params: view.params,
      close: () => {
        if (guard.current !== null && !guard.current()) return
        close(reference)
      },
      setCloseGuard: (next) => {
        guard.current = next
      },
      replaceWith: (next, params) => replace(reference, shell.refOf(next, params)),
      proceedTo: (path) => {
        // Новое окно вернёт туда, где был человек, — уже без этого окна в стопке.
        const state: WindowNavigationState = { windowFrom: `${pathname}${withoutRef(search, reference)}` }
        void navigate(path, { state })
      },
    }),
    [close, navigate, pathname, reference, replace, search, shell, view],
  )

  const Content = shell.renderer(view.kind)
  if (Content === undefined) return null

  return (
    <WindowContext.Provider value={handle}>
      <Frame title={view.title} size={view.size ?? 'lg'} source={view.source} resourceId={view.resourceId} covered={covered} onClose={handle.close}>
        <Suspense fallback={<p role="status">Загрузка…</p>}>{createElement(Content)}</Suspense>
      </Frame>
    </WindowContext.Provider>
  )
}
